import { isUkPostcode } from '@gumtree/shared/src/util/postcode-validation';

export interface ErrorType {
    lastname?: string | string[];
    username?: string | string[];
    firstname?: string;
    password?: string;
    confirmedPassword?: string;
    terms?: string;
    contactphone?: string;
    currentPassword?: string;
    birthdate?: string;
    postcode?: string;
}

export enum ValidationLength {
    min = 8,
    max = 100,
}

export enum ValidationAges {
    min = 16,
    max = 120,
}

export const validateDOB = (dob: string): string => {
    let errorMessage = '';

    if (!dob) {
        return (errorMessage = 'Please enter a date of birth');
    }

    const [day, month, year] = dob.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day);

    if (!day || !month || !year) {
        return 'Please enter a full date of birth';
    }

    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const hasBirthdayPassed =
        today.getMonth() > birthDate.getMonth() ||
        (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

    const actualAge = age > 0 && !hasBirthdayPassed ? age - 1 : age;

    if (actualAge < 0) {
        errorMessage = 'Please enter a date in the past';
    } else if (actualAge < ValidationAges.min) {
        errorMessage = 'Sorry, you must be 16 or over to create an account';
    } else if (actualAge > ValidationAges.max) {
        errorMessage = 'Please enter a valid date of birth';
    }

    return errorMessage;
};

export const validatePostcode = (postcode: string): string => {
    let errorMessage = '';

    if (!postcode) {
        return (errorMessage = 'Please enter a postcode');
    }

    const isValid = isUkPostcode(postcode);

    if (!isValid) {
        return (errorMessage = 'Please enter a valid postcode');
    }

    return errorMessage;
};

const validatePassword = (password: string, isLogin: boolean): string | any => {
    let errorMessage = '';

    if (!password) {
        errorMessage = 'Please enter a password';
    } else if (password.length > ValidationLength.max && !isLogin) {
        errorMessage = 'Your password cannot exceed 100 characters';
    } else if (
        !isLogin &&
        (!/^(?=.*[0-9_])(?=.*[a-z])(?=.*[A-Z])(?=.*[^\w\s])([a-zA-Z0-9_]|[^\w\s])*$/.test(
            password
        ) ||
            password.length < 8)
    ) {
        errorMessage = `Too short. Please enter at least 8 characters.
            Include at least one capital letter, one lowercase letter, one number and one special character
            e.g. !@#$£%^*-_+=`;
    } else {
        /** password passed all validation conditions */
        return '';
    }

    return errorMessage;
};

export const validateEmail = (email: string): string => {
    let errorMessage = '';

    if (!email) {
        errorMessage = 'Please enter a valid email address';
    } else if (
        !/^[A-Z0-9!#$%&'*+/=?^_`{|}~.-]+@[A-Z0-9]([A-Z0-9-]*[A-Z0-9])?(\.[A-Z0-9]([A-Z0-9-]*[A-Z0-9])?)*(\.[A-Z]{2,})?$|^\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\]$/i.test(
            email
        )
    ) {
        errorMessage = 'Please enter a valid email address';
    } else if (email.length > ValidationLength.max) {
        errorMessage = 'The email address cannot exceed 100 characters';
    } else {
        /** email passed all validation conditions */
        return '';
    }

    return errorMessage;
};

export const validateTerms = (isUnchecked: boolean): ErrorType => {
    const errors: ErrorType = {};
    if (isUnchecked) {
        errors.terms = 'You must agree to continue';
    }
    return errors;
};

export const validatePhone = (phone: number): string => {
    let errorMessage = '';
    if (phone?.toString().length < 7) {
        errorMessage = 'Please enter a phone number over 6 numbers';
    }
    return errorMessage;
};

export const validatePasswordMatch = (password: string, confirmedPassword: string): string => {
    let errorMessage = '';
    if (password !== confirmedPassword) {
        errorMessage = 'Your passwords need to match';
    }
    return errorMessage;
};

export const regexFirstName = /^(?!(?:\d+$)|[\+#]\d+$|gumtree$).+$/i;

const validate = (values, eventTarget?, isLogin?, showDateOfBirthInput?, showPostcodeInput?) => {
    const errors: ErrorType = {};
    const target = eventTarget?.toLowerCase();

    if (target === 'firstname' || target === 'all') {
        if (!values.firstname?.trim()) {
            errors.firstname = 'Please enter your first name';
        } else if (values.firstname?.trim().length > ValidationLength.max) {
            errors.firstname = 'The first name cannot exceed 100 characters';
        } else if (!regexFirstName.test(values.firstname?.trim())) {
            errors.firstname = 'Please provide a valid name';
        }
    }

    if (target === 'lastname' || target === 'all') {
        if (!values.lastname?.trim()) {
            errors.lastname = 'Please enter your last name';
        } else if (values.lastname?.trim().length > ValidationLength.max) {
            errors.lastname = 'The last name cannot exceed 100 characters';
        }
    }

    if (target === 'login' || target === 'username' || target === 'email' || target === 'all') {
        const errorMessage = validateEmail(values.username?.trim());

        if (errorMessage) {
            errors.username = errorMessage;
        }
    }

    if (showDateOfBirthInput && (target === 'birthdate' || target === 'all')) {
        const errorMessage = validateDOB(values.birthdate);

        if (errorMessage) {
            errors.birthdate = errorMessage;
        }
    }

    if (showPostcodeInput && (target === 'postcode' || target === 'all')) {
        const errorMessage = validatePostcode(values.postcode);

        if (errorMessage) {
            errors.postcode = errorMessage;
        }
    }

    if (target === 'contactphone' || target === 'all') {
        const errorMessage = validatePhone(values.contactphone?.trim());

        if (errorMessage) {
            errors.contactphone = errorMessage;
        }
    }

    if (
        target === 'login' ||
        target === 'password' ||
        target === 'confirmedPassword' ||
        target === 'all'
    ) {
        const errorMessage = validatePassword(values.password?.trim(), isLogin);

        if (errorMessage) {
            errors.password = errorMessage;
        }

        if (target === 'confirmedPassword' && values.password !== values.confirmedPassword) {
            errors.confirmedPassword = 'Passwords do not match';
        }
    }

    return errors;
};

export default validate;
